import { useEffect } from "react";
import { Routes, Route, useLocation, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import ReactGA from "react-ga4";
import { isEmpty } from "lodash";
import classnames from "classnames";

// Mui components
import { Typography, Container, Box, Button } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Theme } from "@mui/material/styles";

// Context & Hooks
import useAuth from "utils/hooks/useAuth";
import { useAbility } from "@casl/react";
import { AbilityContext } from "context/Ability";
import { useAppSelector, useAppDispatch } from "app/hooks";

// Services
import { logout, updateUserOrg } from "services";

// Store
import { getUserOrgs, fetchUserOrgsThunk } from "store/slice/sysadmin/userOrgs/userOrgsSlice";

// Enums
import icons from "enums/icons";
import { urlPaths } from "enums/urlPaths";

// Components
import Home from "components/Home";
import CustomIcon from "./CustomIcon";
import Loader from "components/Loader";
import { Toast } from "components/Toast";
import NotFound from "components/NotFound";
import Loading from "components/Layout/Loading";
import { SideBar } from "components/Layout/SideBar";
import ReportStatusLoader from "./ReportStatusLoader";
import MainLayout from "components/Layout/MainLayout";
import { fetchSession } from "store/slice/auth";

const useStyles = makeStyles((theme: Theme) => ({
    appBody: {
        ...theme.mixins.appBody,
        padding: theme.spacing(0, 0, 0, 0),
        "&::before": {
            content: '""',
            width: "100%",
            height: 250,
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: -99999,
        },
    },
    appBodyPace: {
        ...theme.mixins.appBody,
    },
}));

const AuthWrapper = ({ children }: { children: React.ReactNode }) => {
    const classes = useStyles();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Url params
    const defaultOrg = searchParams.get("default_org");
    const lastPathname = localStorage.getItem("lastPathname");

    const { pathname, search } = useLocation();
    const { hasSession, sessionData, loggedInUserData, fetched, error } = useAuth();

    const ability = useAbility(AbilityContext);
    const userOrgs = useAppSelector(getUserOrgs);

    const isLoading =
        (fetched && error && error.code === 401) || (!fetched && !hasSession) || userOrgs.status === "fetching";
    const hasBasicRoles =
        ability.can("see", "LD_FREE") ||
        ability.can("see", "LD_BASIC") ||
        ability.can("see", "LD_LIMITED") ||
        ability.can("see", "PACE") ||
        ability.can("see", "SAAS_LD_PROJECT") ||
        ability.can("see", "SAAS_PACE");

    if (defaultOrg && !lastPathname?.includes("default_org")) {
        localStorage.removeItem("lastPathname");
        localStorage.setItem("lastPathname", `${pathname}?default_org=${defaultOrg}`);
    }

    // Fetch User Organisations
    useEffect(() => {
        const getUserOrgs = async () => {
            await dispatch(fetchUserOrgsThunk());
        };

        if (!userOrgs.data.length && userOrgs.status === "pristine") {
            getUserOrgs();
        }
    }, [dispatch, userOrgs]);

    // Check organisations and redirect the user accordingly
    useEffect(() => {
        if (!userOrgs.data.length || sessionData === null || !hasBasicRoles || loggedInUserData.selectedOrg?.id) {
            return;
        }

        const handleUpdateSelectedOrg = async (orgId: number) => {
            const { data } = await updateUserOrg(orgId);

            if (data.Info === "OK") {
                try {
                    await dispatch(fetchSession());
                } catch (err) {
                    console.error(err);
                }
            }
        };

        const handleSetDefaultOrg = async (org: string) => {
            const orgToNumber = parseInt(org);

            if (isNaN(orgToNumber)) {
                console.error("Invalid default_org");
                return;
            }

            await handleUpdateSelectedOrg(orgToNumber);
        };

        const orgsCount = userOrgs.data.length;
        const allOrgsAreProfile = Boolean(
            orgsCount && userOrgs.data.every((org) => org.user.function_name === "PROFILE")
        );
        const orgsUserIsNotProfile = userOrgs.data.filter((org) => org.user.function_name !== "PROFILE");

        // handleSetDefaultOrg should execute the useEffect in Home.tsx redirecting the user to the corresponding page

        // Redirect to Home
        if (orgsCount === 0) {
            navigate(urlPaths.Home);
        }

        // If default org is present in URL
        if (defaultOrg) {
            handleSetDefaultOrg(defaultOrg);
            return;
        }

        // Profile only in all hubs OR has only on hub
        if (allOrgsAreProfile || orgsCount === 1) {
            const orgId = userOrgs.data[0].id;
            handleUpdateSelectedOrg(orgId);
            return;
        }

        // Redirect to Select hub page
        if (orgsCount > 1 && orgsUserIsNotProfile.length) {
            navigate(urlPaths.ChooseYourHub);
        }
    }, [dispatch, hasBasicRoles, loggedInUserData.selectedOrg?.id, navigate, sessionData, userOrgs.data, defaultOrg]);

    return (
        <>
            {hasSession && hasBasicRoles && (
                <>
                    <SideBar pathname={`${pathname}${search}`} loggedInUserData={loggedInUserData} />
                    <Container
                        maxWidth={false}
                        className={classnames(classes.appBody, {
                            [classes.appBodyPace]: pathname.includes("pace"),
                        })}
                    >
                        {children}
                    </Container>
                </>
            )}

            {isLoading && <Loading />}

            {hasSession && fetched && isEmpty(error) && !hasBasicRoles && !isLoading && (
                <Box
                    width="100vw"
                    height="100vh"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                >
                    <CustomIcon icon={icons.lock}></CustomIcon>
                    <Typography color="text.primary" variant="h4">
                        Access restricted
                    </Typography>
                    <Typography color="text.disabled" variant="h6" sx={{ fontSize: 16 }}>
                        You don&lsquo;t have enough permission to view this page. Please contact the administrator.
                    </Typography>
                    <Button variant="text" onClick={() => logout()}>
                        Log out
                    </Button>
                </Box>
            )}
        </>
    );
};

const App = (): JSX.Element => {
    const location = useLocation();

    useEffect(() => {
        ReactGA.send({ hitType: "pageview", page: location?.pathname });
    }, [location]);

    return (
        <>
            <Loader />
            <Toast />
            <ReportStatusLoader />
            <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/choose-you-hub" element={<Navigate to={urlPaths.ChooseYourHub} />} />;
                <Route path="/leadership-dynamics" element={<Navigate to={urlPaths.Search} />} />
                <Route path="/account-management" element={<Navigate to={urlPaths.AccountManagement} />} />
                <Route
                    path="/choose-your-hub/*"
                    element={
                        <AuthWrapper>
                            <MainLayout parentRoute="/choose-your-hub" />
                        </AuthWrapper>
                    }
                />
                <Route
                    path="/leadership-dynamics/*"
                    element={
                        <AuthWrapper>
                            <MainLayout parentRoute="/leadership-dynamics" />
                        </AuthWrapper>
                    }
                />
                <Route
                    path="/pace/*"
                    element={
                        <AuthWrapper>
                            <MainLayout parentRoute="/pace" />
                        </AuthWrapper>
                    }
                />
                <Route
                    path="/talent/*"
                    element={
                        <AuthWrapper>
                            <MainLayout parentRoute="/talent" />
                        </AuthWrapper>
                    }
                />
                <Route
                    path="/account-management/*"
                    element={
                        <AuthWrapper>
                            <MainLayout parentRoute="/account-management" />
                        </AuthWrapper>
                    }
                />
                <Route
                    path="/hub-management/*"
                    element={
                        <AuthWrapper>
                            <MainLayout parentRoute="/hub-management" />
                        </AuthWrapper>
                    }
                />
                <Route
                    path="/user-management/*"
                    element={
                        <AuthWrapper>
                            <MainLayout parentRoute="/user-management" />
                        </AuthWrapper>
                    }
                />
                <Route
                    path="/leadership-dashboard/*"
                    element={
                        <AuthWrapper>
                            <MainLayout parentRoute="/leadership-dashboard" />
                        </AuthWrapper>
                    }
                />
                <Route
                    path={urlPaths.NotFound}
                    element={
                        <AuthWrapper>
                            <NotFound />
                        </AuthWrapper>
                    }
                />
                <Route path="*" element={<Navigate to={urlPaths.NotFound} />} />
            </Routes>
        </>
    );
};

export default App;
